/**
 * Price Utilities
 * 
 * Frame token price in USD - REAL prices only, no hardcoded fallbacks.
 * Users need accurate prices to make spending decisions.
 */

// Price API endpoint
const PRICE_API_URL = 'https://nt3d-price-api.wurkit.workers.dev/api/price/frame';

// Cache
let cachedPrice: number = 0;
let lastFetchTime: number = 0;
const CACHE_DURATION = 30000; // 30 seconds - match the API cache

// Track if we've ever successfully fetched a price
let hasFetchedPrice = false;

export interface FramePriceData {
  price: number;
  change24h: number;
  volume24h: number;
  source?: string;
}

let cachedFrameData: FramePriceData = { price: 0, change24h: 0, volume24h: 0 };
let lastFrameFetchTime: number = 0;

/**
 * Fetch Frame price with 24h change.
 */
export async function fetchFramePriceData(): Promise<FramePriceData> {
  const now = Date.now();
  
  if (now - lastFrameFetchTime < CACHE_DURATION && cachedFrameData.price > 0) {
    return cachedFrameData;
  }
  
  const price = await fetchFramePrice();
  cachedFrameData = { price, change24h: 0, volume24h: 0, source: 'mockframe-testnet' };
  lastFrameFetchTime = now;
  
  return cachedFrameData;
}

/**
 * Fetch Frame price in USD.
 * Uses our price-api worker first, falls back to testnet estimate.
 * Returns 0 if price unavailable - NO HARDCODED FALLBACK.
 */
export async function fetchFramePrice(): Promise<number> {
  const now = Date.now();
  
  // Use cache if fresh and valid
  if (now - lastFetchTime < CACHE_DURATION && cachedPrice > 0) {
    return cachedPrice;
  }
  
  // Try our price API first
  try {
    const response = await fetch(PRICE_API_URL, {
      headers: { 'Accept': 'application/json' },
      signal: AbortSignal.timeout(5000)
    });
    
    if (response.ok) {
      const data = await response.json();
      if (data.price && data.price > 0) {
        cachedPrice = data.price;
        lastFetchTime = now;
        hasFetchedPrice = true;
        console.log(`Frame price from API: $${data.price} (source: ${data.source || 'unknown'}, chain: ${data.chain || 'unknown'})`);  
        return cachedPrice;
      }
    }
  } catch (error) {
    console.warn('Price API failed, trying direct DexScreener:', error);
  }
  
  // Fallback: Testnet estimate (MockFrame has no real price)
  // Conservative estimate based on March 20 PAW snapshot value
  // Real Frame price TBD once Frame Network mainnet launches
  console.warn('Frame price unavailable - using testnet estimate');
  
  // Return cached price if we have one (even if stale), otherwise 0
  if (cachedPrice > 0) {
    console.warn('Using stale cached price:', cachedPrice);
    return cachedPrice;
  }
  
  console.error('Frame price unavailable - no valid source');
  return 0;
}

/**
 * Fetch Frame price from DexScreener (when Frame Network launches).
 * Currently returns 0 for MockFrame testnet token.
 */
export async function fetchFramePriceFromDexScreener(): Promise<number> {
  // MockFrame on Base Sepolia has no DexScreener listing
  // Return 0 until Frame Network mainnet launches
  return 0;
}

/**
 * Check if we have a valid price (has been fetched at least once)
 */
export function hasValidPrice(): boolean {
  return hasFetchedPrice && cachedPrice > 0;
}

/**
 * Get the current cached price without fetching
 */
export function getCachedPrice(): number {
  return cachedPrice;
}

/**
 * Format Frame amount to USD string
 */
export function formatFrameToUSD(frameAmount: bigint, framePrice: number): string {
  if (framePrice <= 0) {
    return '--'; // No price available
  }
  
  const frameNumber = Number(frameAmount) / 1e18;
  const usdValue = frameNumber * framePrice;
  
  if (usdValue >= 1e12) {
    return (usdValue / 1e12).toFixed(2) + 'T';
  } else if (usdValue >= 1e9) {
    return (usdValue / 1e9).toFixed(2) + 'B';
  } else if (usdValue >= 1e6) {
    return (usdValue / 1e6).toFixed(2) + 'M';
  } else if (usdValue >= 1000) {
    return (usdValue / 1000).toFixed(2) + 'K';
  } else if (usdValue >= 1) {
    return usdValue.toFixed(2);
  } else if (usdValue >= 0.01) {
    return usdValue.toFixed(4);
  } else if (usdValue > 0) {
    return usdValue.toFixed(6);
  } else {
    return '0.00';
  }
}


